/**
 * 設備の表記に関するユーティリティ
 * https://www.notion.so/sanu/Feature-26bd14f248a880d0946ac2d0f0f7f5e8
 */

import { AccommodationFacilityType } from "@sa-nu/core-system/src/graphql/__generated__/types";

// 対応言語
type Language = "ja" | "en";

// 規定の表記
const accommodationFacilityLabels: Record<
  AccommodationFacilityType,
  Record<Language, string>
> = {
  DogFriendly: {
    ja: "ドッグフレンドリー",
    en: "Dog Friendly",
  },
  Onsen: {
    ja: "天然温泉",
    en: "Onsen",
  },
  DogRun: {
    ja: "ドッグラン",
    en: "Dog Run",
  },
  Sauna: {
    ja: "サウナ",
    en: "Sauna",
  },
  EVCharger: {
    ja: "EV充電器",
    en: "EV Charger",
  },
  Workation: {
    ja: "ワーケーション",
    en: "Workation",
  },
};

// 規定の並び順
const orderOfAccommodationFacilities: AccommodationFacilityType[] = [
  AccommodationFacilityType.Onsen,
  AccommodationFacilityType.Sauna,
  AccommodationFacilityType.DogFriendly,
  AccommodationFacilityType.DogRun,
  AccommodationFacilityType.Workation,
  AccommodationFacilityType.EVCharger,
];

// 予約画面等での並び順
const orderOfAccommodationFacilitiesForReservation: AccommodationFacilityType[] =
  [
    AccommodationFacilityType.Sauna,
    AccommodationFacilityType.Onsen,
    AccommodationFacilityType.Workation,

    // オプション利用有無を選択できるもの
    AccommodationFacilityType.DogFriendly,
    AccommodationFacilityType.EVCharger,

    // NOTE: DogRunは表示しない
  ];

/**
 * 設備のラベルを取得する。
 *
 * @param type 設備のタイプ
 * @param lang 言語
 * @returns 設備のラベル
 */
export function getAccommodationFacilityLabel(
  type: AccommodationFacilityType,
  lang: Language = "ja",
): string {
  switch (type) {
    case AccommodationFacilityType.DogFriendly:
      return accommodationFacilityLabels.DogFriendly[lang];
    case AccommodationFacilityType.Onsen:
      return accommodationFacilityLabels.Onsen[lang];
    case AccommodationFacilityType.Sauna:
      return accommodationFacilityLabels.Sauna[lang];
    case AccommodationFacilityType.Workation:
      return accommodationFacilityLabels.Workation[lang];
    case AccommodationFacilityType.EVCharger:
      return accommodationFacilityLabels.EVCharger[lang];
    case AccommodationFacilityType.DogRun:
      return accommodationFacilityLabels.DogRun[lang];
    default: {
      const _exhaustiveCheck: never = type;
      return "";
    }
  }
}

function getAccommodationFacilityIconPath(
  type: AccommodationFacilityType,
): string {
  switch (type) {
    case AccommodationFacilityType.Sauna:
      return "/i/icon_sauna.svg";
    case AccommodationFacilityType.Onsen:
      return "/i/icon_onsen.svg";
    case AccommodationFacilityType.Workation:
      return "/i/icon_workation.svg";
    case AccommodationFacilityType.DogFriendly:
      return "/i/icon_dogfriendly.svg";
    case AccommodationFacilityType.EVCharger:
      return "/i/icon_evstation.svg";
    case AccommodationFacilityType.DogRun:
      return "/i/icon_dogrun.svg";
    default: {
      const _exhaustiveCheck: never = type;
      return "";
    }
  }
}

/**
 * 設備を、規定した並び順で列挙したラベルを取得する。
 *
 * @param types 設備のタイプの配列
 * @param lang 言語
 * @returns 設備のラベル（例: 「サウナ / ドッグフレンドリー / EV充電器」）
 */
export function getAccommodationFacilitiesLabel(
  types: AccommodationFacilityType[],
  lang: Language = "ja",
): string {
  return orderOfAccommodationFacilities
    .filter((type) => types.includes(type))
    .map((type) => getAccommodationFacilityLabel(type, lang))
    .join(" / ");
}

/**
 * 設備のラベルを予約画面用の順で取得する。
 *
 * @param facilities 設備のタイプのセット
 * @param lang 言語
 * @returns 設備のラベルの配列
 */
export function getAccommodationFacilityLabelsForReservation(
  facilities: Set<AccommodationFacilityType>,
  lang: Language = "ja",
): string[] {
  return orderOfAccommodationFacilitiesForReservation
    .filter((facility) => facilities.has(facility))
    .map((facility) => getAccommodationFacilityLabel(facility, lang));
}

/**
 * 設備のアイコンパスを予約画面用の順で取得する。
 *
 * @param facilities 設備のタイプのセット
 * @returns 設備のアイコンパスの配列
 */
export function getAccommodationFacilityIconPathsForReservation(
  facilities: Set<AccommodationFacilityType>,
): string[] {
  return orderOfAccommodationFacilitiesForReservation
    .filter((facility) => facilities.has(facility))
    .map((facility) => getAccommodationFacilityIconPath(facility));
}

/**
 * 予約画面用の設備のラベルとアイコンパスを取得する。
 *
 * @param facilities 設備のタイプのセット
 * @param lang 言語
 * @returns 設備の情報（ラベルとアイコンパス）の配列
 */
export function getAccommodationFacilityLabelsAndIconPathsForReservation(
  facilities: Set<AccommodationFacilityType>,
  lang: Language = "ja",
): Array<{ type: AccommodationFacilityType; label: string; iconPath: string }> {
  return orderOfAccommodationFacilitiesForReservation
    .filter((facility) => facilities.has(facility))
    .map((facility) => ({
      type: facility,
      label: getAccommodationFacilityLabel(facility, lang),
      iconPath: getAccommodationFacilityIconPath(facility),
    }));
}
